import React from 'react';
import PropTypes from 'prop-types';
import ReactSelect, { components } from 'react-select';
import ReactSelectAsync from 'react-select/async';
import debounce from 'lodash.debounce';
import { SvgChevron } from '../../svg';
import { styles } from './styles';
import { useTheme } from '../../theme';

const debouncedFetch = debounce((fn, searchTerm, callback) => {
  fn(searchTerm, callback);
}, 500);

const DropdownIndicator = (props) => {
  const theme = useTheme();

  return (
    <components.DropdownIndicator {...props}>
      <SvgChevron
        stroke={theme.colors.primary}
        /* eslint-disable-next-line react/prop-types */
        direction={props.selectProps.menuIsOpen ? 'top' : 'bottom'}
      />
    </components.DropdownIndicator>
  );
};

function Select({
  id,
  name,
  onChange,
  onBlur,
  onFocus,
  onKeyDown,
  style,
  options,
  error,
  defaultOpen,
  value,
  accessor = 'value',
  getFullOption,
  title,
  async,
  asyncOptions,
  maxViewItems = 6,
  isMulti,
  addEmptyOption,
  placeholder,
  autoFocus,
  disabled,
  ...rest
}) {
  const theme = useTheme();
  const [loadOptions, setLoadOptions] = React.useState([]);
  const [stateValue, setStateValue] = React.useState(value);

  async function handleLoadOptions(inputValue, callback) {
    if (inputValue && inputValue.length > 1) {
      setStateValue(inputValue);
      const res = await asyncOptions.api(inputValue);
      setLoadOptions((o) => [...o, ...asyncOptions.accessor(res)]);
      callback(asyncOptions.accessor(res));
    }
  }

  const preparedOptions = addEmptyOption
    ? [{ label: '', [accessor]: undefined }, ...options]
    : options;

  function handleChange(option) {
    if (typeof onChange === 'function') {
      if (getFullOption) {
        onChange(option);
      } else if (isMulti) {
        onChange(option && option.map((o) => o[accessor]));
      } else {
        onChange(option[accessor]);
      }
    }
  }

  function handleValue() {
    if (async) {
      if (typeof value === 'string') {
        return loadOptions?.find((o) => o[accessor] === value) ?? null;
      }
      return value;
    }
    if (getFullOption) {
      if (typeof value === 'string') {
        return options?.find((o) => o[accessor] === value) ?? null;
      }
      return value;
    }
    if (isMulti) {
      return options?.filter((o) => value.includes(o[accessor]));
    }
    return options?.find((o) => o[accessor] === value);
  }

  const selectValue = handleValue();

  return async ? (
    <ReactSelectAsync
      autoFocus={autoFocus}
      placeholder={placeholder}
      id={id}
      inputValue={stateValue}
      name={name}
      cacheOptions
      defaultOptions
      loadOptions={(v, c) => debouncedFetch(handleLoadOptions, v, c)}
      value={selectValue}
      components={{ DropdownIndicator }}
      styles={styles(theme, style, maxViewItems, error)}
      onChange={handleChange}
      onBlur={onBlur}
      onFocus={onFocus}
      onKeyDown={onKeyDown}
      onInputChange={setStateValue}
      isMulti={isMulti}
      isDisabled={disabled}
      {...rest}
    />
  ) : (
    <ReactSelect
      autoFocus={autoFocus}
      placeholder={placeholder}
      id={id}
      name={name}
      value={selectValue}
      components={{ DropdownIndicator }}
      styles={styles(theme, style, maxViewItems, error)}
      onChange={handleChange}
      onBlur={onBlur}
      onFocus={onFocus}
      onKeyDown={onKeyDown}
      options={preparedOptions}
      isMulti={isMulti}
      isDisabled={disabled}
      {...rest}
    />
  );
}

Select.propTypes = {
  maxViewItems: PropTypes.number,
  defaultOpen: PropTypes.bool,
  style: PropTypes.object,
  options: PropTypes.array,
  error: PropTypes.bool,
  name: PropTypes.string,
  accessor: PropTypes.string,
  getFullOption: PropTypes.bool,
  onChangeOverride: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.array]),
  title: PropTypes.string,
  placeholder: PropTypes.string,
  id: PropTypes.string,
  onKeyDown: PropTypes.func,
  isMulti: PropTypes.bool,
  addEmptyOption: PropTypes.bool,
  autoFocus: PropTypes.bool,
  async: PropTypes.bool,
  asyncOptions: PropTypes.object,
  disabled: PropTypes.bool,
};

export default React.memo(Select);
